import _ from 'lodash';
import { isSectionNotInRange, getNextDay, filterSections } from '../../utils';
import moment from 'moment';
import {
  ITEM_KEY_CATEGORY_SEPARATOR,
  ITEM_KEY_COUNTER_SEPARATOR,
  OFFER_SECTION,
} from '../../constants';
import { BuyNGetMMarketingCampaignConfig, MarketingCampaignType, Offer } from '../../1_types/genericTypes';
import { buildBngmOfferProduct, isBngmOptionListOffer } from '../../utils/bngmOffer';
import { getBngmOptionListKey, getOfferConfigProducts, isOptionListTarget } from '../../utils/marketingCampaign';

const getTrueEndDate = (
  startDate: any,
  endDate: any,
  daysOfWeek: any
): string => {
  const start = moment(startDate);
  const end = moment(endDate);
  const daysOfWeekArray = Object.keys(daysOfWeek);

  let lastTargetDay = null;

  for (let m = start; m.isSameOrBefore(end); m.add(1, 'days')) {
    if (daysOfWeekArray.includes(m.format('dddd'))) {
      lastTargetDay = moment(m);
    }
  }

  return lastTargetDay ? lastTargetDay.locale('fr').format('DD MMMM') : null;
};

export const updateMsgSectionRange = (
  sections: any,
  messages: any,
  setMessages: React.Dispatch<React.SetStateAction<any>>
): void => {
  if (!sections?.some((section) => section?.activationRange)) return;

  const currentDate = moment();
  const messagesToUpdate = {};

  sections.forEach((section) => {
    const { activationRange, key } = section;
    if (!activationRange) return;

    const isOutOfSchedule = isSectionNotInRange(section);
    if (!isOutOfSchedule) {
      if (messages[key]) messagesToUpdate[key] = null;
      return;
    }

    const { startDate, endDate, daysOfWeek } = activationRange;
    const oneWeekBeforeStartDate = moment(startDate).subtract(7, 'days');

    if (!currentDate.isSameOrAfter(oneWeekBeforeStartDate)) return;

    const dayOfWeek = currentDate.format('dddd');
    const currentDayConfig = daysOfWeek[dayOfWeek];
    const currentSlots = currentDayConfig?.slots || [];

    let activationRangeString = '';

    if (currentDate.isAfter(endDate)) {
      messagesToUpdate[key] = null;
    } else if (currentDate.isBefore(startDate)) {
      const nextDay = getNextDay(daysOfWeek, startDate, endDate);
      const formattedStartDate = daysOfWeek[moment(startDate).format('dddd')]
        ? moment(startDate).locale('fr').format('DD MMMM')
        : nextDay
          ? nextDay.split(' ').slice(1).join(' ')
          : '';
      const formattedEndDate = daysOfWeek[moment(endDate).format('dddd')]
        ? moment(endDate).locale('fr').format('DD MMMM')
        : getTrueEndDate(startDate, endDate, daysOfWeek);

      activationRangeString =
        formattedStartDate === formattedEndDate || !formattedEndDate
          ? `le ${formattedStartDate}`
          : `du ${formattedStartDate} au ${formattedEndDate}`;
    } else {
      const nextDay = getNextDay(daysOfWeek, startDate, endDate, currentDate);
      const validSlot =
        currentSlots.length && !currentDayConfig?.fullDay
          ? currentSlots.find((slot) =>
            currentDate.isBefore(moment(slot.split('-')[1], 'HH:mm'))
          )
          : null;

      activationRangeString = validSlot
        ? `de ${validSlot.replace('-', ' à ')}`
        : nextDay;
    }

    if (!_.isEqual(messages[key], activationRangeString)) {
      messagesToUpdate[key] = activationRangeString;
    }
  });

  if (Object.keys(messagesToUpdate).length > 0) {
    setMessages((prev) => ({ ...prev, ...messagesToUpdate }));
  }
};

export const isCheckedOrSelected = (
  productItems: any,
  index: number,
  item: any
): boolean => {
  return !!_.find(
    productItems?.[index]?.options,
    (option) =>
      option?.itemRecursiveKey &&
      option.itemRecursiveKey === generateItemRecursiveKey(item, undefined)
  );
};

export const generateItemRecursiveKey = (item: any, counter: any): string => {
  const itemInitialKey = `${item.categoryKey}${ITEM_KEY_CATEGORY_SEPARATOR}${item.key}`;
  return counter !== undefined && parseInt(counter, 10) >= 0
    ? `${itemInitialKey}${ITEM_KEY_COUNTER_SEPARATOR}${counter}`
    : itemInitialKey;
};

export const getNewSections = (
  productOffers: Offer[],
  sections: any,
  bngmOffers?: Offer[],
  shopCategories?: any
): any[] => {
  const tempSections = [];
  const hasBngm = bngmOffers?.length > 0;

  if (productOffers?.length || hasBngm) {
    const offersSection = { ...OFFER_SECTION };
    offersSection.products = mapProductFromOffers(productOffers, sections);

    if (hasBngm && shopCategories) {
      for (const offer of bngmOffers) {
        const config = offer.config as BuyNGetMMarketingCampaignConfig;
        if (!isOptionListTarget(config.target)) continue;
        const optionListKey = getBngmOptionListKey(config);
        const optionListCategory = optionListKey ? shopCategories[optionListKey] : null;
        if (!optionListCategory) continue;
        const virtualProduct = buildBngmOfferProduct(offer, optionListCategory, sections);
        if (virtualProduct) {
          offersSection.products[virtualProduct.key] = virtualProduct;
        }
      }
    }

    tempSections.push(offersSection);
  }
  tempSections.push(...(Array.isArray(sections) ? sections : []));
  return filterSections(tempSections);
};

type Section = {
  key: string;
  products: { [key: string]: any };
};

export const mapProductFromOffers = (
  offers: Offer[],
  sections: Section[] | null | undefined
): { [key: string]: any } => {
  const products: { [key: string]: any } = {};

  const getProduct = (sectionKey: string, productKey: string) => {
    if (!Array.isArray(sections)) return undefined;
    const section = sections.find((s) => s.key === sectionKey);
    return section?.products?.[productKey];
  };

  for (const offer of offers) {
    const offerType = offer.type;
    const offerProducts = getOfferConfigProducts(offer.config);
    if (!offerProducts?.length) continue;

    for (const item of offerProducts) {
      const {
        productKey,
        productSectionKey,
        productOfferedKey,
        productOfferedSectionKey,
      } = item;

      const mainProduct = getProduct(productSectionKey, productKey);
      const offeredProduct = getProduct(productOfferedSectionKey, productOfferedKey);

      switch (offerType) {
        case MarketingCampaignType.PRODUCT_DISCOUNT:
        case MarketingCampaignType.PRODUCT_DISCOUNT_AFTER_THRESHOLD:
        case MarketingCampaignType.GLOBAL_PRODUCT_DISCOUNT_AFTER_THRESHOLD:
        case MarketingCampaignType.BUY_N_GET_M_CHEAPEST_AT_DISCOUNT:
          if (mainProduct) {
            products[productKey] = {
              ...mainProduct,
              sectionKey: productSectionKey,
            };
          }
          break;

        case MarketingCampaignType.BUY_ONE_GET_ONE_PRODUCT:
        case MarketingCampaignType.BUY_ONE_GET_ONE_DIFFERENT_PRODUCT:
          if (mainProduct && offeredProduct) {
            products[productKey] = {
              ...mainProduct,
              sectionKey: productSectionKey,
              offeredKey: productOfferedKey,
              offeredSectionKey: productOfferedSectionKey,
            };
          }
          break;

        default:
          break;
      }
    }
  }

  return products;
};
